import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ArrowUpRight, ChevronDown } from 'lucide-react';
import MagneticButton from './MagneticButton';
import { STATIC } from '@/lib/cms';
import BrandMark from './BrandMark';

const nav = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/services', label: 'Services', mega: true },
  { to: '/portfolio', label: 'Work' },
  // Pricing hidden for now — uncomment to restore.
  // { to: '/pricing', label: 'Pricing' },
  { to: '/blog', label: 'Journal' },
  { to: '/contact', label: 'Contact' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mega, setMega] = useState(false);
  const closeTimer = useRef(null);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); setMega(false); }, [loc.pathname]);

  useEffect(() => {
    if (open) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.position = '';
        document.body.style.width = '';
      };
    }
    return undefined;
  }, [open]);

  const openMega = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } setMega(true); };
  const closeMega = () => { closeTimer.current = setTimeout(() => setMega(false), 140); };
  

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${scrolled ? 'glass-header py-3 lg:py-2' : 'py-5 lg:py-4'}`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-6 md:px-10">
        <Link to="/" className="flex items-center" data-testid="header-logo" data-cursor>
          <BrandMark size="md" />
        </Link>

        {/* Bridge zone: single hover parent that includes nav + mega, so cursor can travel between them without closing */}
        <div
          className="hidden lg:flex flex-1 justify-center"
          onMouseLeave={closeMega}
        >
          <nav className="flex items-center gap-1">
            {nav.map((n) => (
              <div
                key={n.to}
                className="relative"
                onMouseEnter={n.mega ? openMega : undefined}
              >
                <NavLink
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase()}`}
                  className={({ isActive }) => `group flex justify-center px-4 py-2 text-[15px] font-medium transition-colors ${isActive ? 'text-ink-900' : 'text-ink-500 hover:text-ink-900'}`}
                >
                  <span className="inline-flex items-center gap-1">
                    {n.label}
                    {n.mega && <ChevronDown className={`h-3.5 w-3.5 opacity-60 transition-transform ${mega ? 'rotate-180' : ''}`} />}
                  </span>
                  <span
                    className="pointer-events-none absolute inset-x-3 -bottom-0.5 h-px origin-left scale-x-0 bg-cyan transition-transform duration-300 group-hover:scale-x-100"
                    aria-hidden
                  />
                </NavLink>
              </div>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="header-cta"
            className="hidden lg:inline-block"
            data-cursor
          >
            <MagneticButton
              as="span"
              className="btn-cyan group rounded-full px-5 py-3 text-sm font-medium whitespace-nowrap"
            >
              Book a consultation
              <ArrowUpRight className="ml-1.5 h-4 w-4 transition-transform group-hover:rotate-45" />
            </MagneticButton>
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(v => !v)}
            className="lg:hidden grid h-11 w-11 place-items-center rounded-full border border-ink-100 bg-white/70 backdrop-blur"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mega menu — no gap between trigger and panel, keep same hover parent so travel doesn't close */}
      <div
        className={`absolute inset-x-0 top-full transition-all duration-300 ${mega ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-2 pointer-events-none'}`}
        onMouseEnter={openMega}
        onMouseLeave={closeMega}
      >
        {/* invisible bridge that connects the trigger row to the panel */}
        <div className="h-3" aria-hidden />
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="glass-card rounded-3xl border border-ink-100 p-8 shadow-medium">
            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              <div>
                <p className="mb-4 text-xs uppercase tracking-[0.25em] text-ink-400">Services</p>
                <div className="space-y-3">
                  {STATIC.services.slice(0, 3).map(s => (
                    <Link key={s.id} to={`/services/${s.slug}`} className="group flex items-start gap-3 rounded-xl p-3 transition-colors hover:bg-ink-50" data-testid={`mega-service-${s.slug}`}>
                      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-cyan-soft text-cyan">
                        <span className="font-display text-sm">0{s.order_index}</span>
                      </div>
                      <div>
                        <p className="font-medium text-ink-900 group-hover:text-cyan">{s.title}</p>
                        <p className="text-sm text-ink-500">{s.tagline}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
              <div>
                <p className="mb-4 text-xs uppercase tracking-[0.25em] text-ink-400">More</p>
                <div className="space-y-3">
                  {STATIC.services.slice(3, 6).map(s => (
                    <Link key={s.id} to={`/services/${s.slug}`} className="group flex items-start gap-3 rounded-xl p-3 transition-colors hover:bg-ink-50">
                      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-cyan-soft text-cyan">
                        <span className="font-display text-sm">0{s.order_index}</span>
                      </div>
                      <div>
                        <p className="font-medium text-ink-900 group-hover:text-cyan">{s.title}</p>
                        <p className="text-sm text-ink-500">{s.tagline}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
              <div className="relative overflow-hidden rounded-2xl bg-ink-900 p-6 text-white">
                <div className="absolute inset-0 hero-gradient-orb opacity-60" />
                <div className="relative">
                  <p className="text-xs uppercase tracking-[0.25em] text-cyan-400/80">Featured</p>
                  <h4 className="mt-2 font-display text-2xl">Free 30-min strategy call</h4>
                  <p className="mt-2 text-sm text-white/70">Walk away with 3 specific wins you can implement this quarter.</p>
                  <Link to="/contact" className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-ink-900">
                    Book free call <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      <div className={`fixed inset-0 z-40 transition-all duration-500 lg:hidden ${open ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'}`}>
        <div className="absolute inset-0 bg-white/60 backdrop-blur-md" onClick={() => setOpen(false)} />
        <div className={`absolute right-0 top-0 h-full w-[88%] max-w-sm max-h-screen overflow-hidden bg-white shadow-2xl transition-transform duration-500 ${open ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex h-full flex-col overflow-y-auto">
            <div className="flex items-center justify-between border-b border-ink-100 px-6 py-5">
              <BrandMark size="sm" />
              <button onClick={() => setOpen(false)} aria-label="Close" className="grid h-10 w-10 place-items-center rounded-full bg-ink-50"><X className="h-5 w-5" /></button>
            </div>
            <nav className="flex flex-col p-4">
              {nav.map(n => (
                <NavLink key={n.to} to={n.to} data-testid={`mobile-nav-${n.label.toLowerCase()}`} className={({isActive}) => `rounded-2xl px-4 py-4 text-lg font-medium transition ${isActive ? 'bg-cyan-soft text-cyan-700' : 'text-ink-900 hover:bg-ink-50'}`}>
                  {n.label}
                </NavLink>
              ))}
              <Link to="/contact" className="btn-cyan mt-4 rounded-full px-5 py-4 text-center font-medium">Book a consultation</Link>
            </nav>
          </div>
        </div>
      </div>
    </header>
  );
}
